/**
 * §9.2's rating board builder as a pure module — every rule on the screen that fails silently.
 *
 * **The screen renders; this decides.** A rating board is five independent JSON documents (§4.1)
 * with rules that span them, and almost none of those rules announce themselves when they go
 * wrong: a period table whose months do not tile its window scores a stretch of the calendar
 * nothing at all; a date range rendered with the wrong month arithmetic is off by a day in
 * February only; a missing §9.4 notice is a best-15 points system quietly ignored for a season.
 * Each of those produces a plausible wrong number rather than an error, which is why this is a
 * module with tests rather than expressions inside a component — `builder.test.js` names the
 * mutation each of them survives.
 *
 * It computes nothing about a rating. The engine owns every figure; what is here is the
 * configuration an operator is composing and the arithmetic needed to *describe* it back to them.
 */

// ---------------------------------------------------------------- §4.4 the window

export type RatingWindowType = 'rolling' | 'fixed' | 'allTime';

export interface RatingWindow {
  type: RatingWindowType;
  /** Rolling only. An exact multiple of `aggregation.periodLengthMonths` (§4.4). */
  lengthMonths: number | null;
  /** Fixed only, both inclusive. */
  startDate: string | null;
  endDate: string | null;
}

// ---------------------------------------------------------------- §3.2 the period calendar

/** One period's rendered bounds — §9.3's "2026-03-31 – 2026-09-30", which is the readable half. */
export interface RatingPeriodRange {
  index: number;
  /** The first date the period includes, or null where it has no lower edge. */
  from: string | null;
  /** The last date it includes. Always known: every window has an upper bound. */
  to: string;
}

/**
 * §2.2's anchor as this screen uses it: the date every period range is measured from, and the
 * date the preview is computed at.
 *
 * <b>The server's `asOfDate` whenever there is one</b>, because the server reads the ROOM's today
 * (`RatingRecalculator.Today`) and the table has to describe the buckets the engine actually
 * filled. The builder used to send its own anchor instead, and it was
 * `new Date().toISOString().slice(0, 10)` — the UTC date — so every evening west of Greenwich the
 * preview was computed at tomorrow's anchor while the recalculation it previews published at
 * today's. It now sends none and reads the answer.
 *
 * <b>Before there is an answer, this computer's calendar date — never the UTC date.</b> A board
 * with no points system chosen cannot be computed at all and still shows its period table, so
 * something has to stand in. The browser's own date is the right stand-in for an operator in the
 * room's zone, which is the ordinary case; for one elsewhere it is a day out at worst, and only
 * until the preview answers.
 */
export function periodAnchor(asOfDate: string | null | undefined, now: Date): string {
  if (asOfDate) return asOfDate;

  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * §3.2's periods, resolved against one anchor and rendered as the dates §9.3 requires.
 *
 * <b>This mirrors `RatingEngine.RatingCalendar` and must keep doing so.</b> The table an operator
 * reads has to describe the buckets the engine actually fills, or the screen explains a rating it
 * is not producing — and the two ways to get it wrong are both invisible on most anchors:
 *
 * 1. <b>Calendar months clamp; they do not overflow.</b> `DateOnly.AddMonths` turns 2026-03-31 back
 *    one month into 2026-02-28. JavaScript's `setMonth` rolls it forward into 2026-03-03 instead,
 *    so a table anchored near the end of a long month is three days out — in February only.
 * 2. <b>Every bound is measured FROM THE ANCHOR</b>, never by stepping back from the period before
 *    it. Chaining compounds each clamp: from 2026-03-31 in one-month periods the edges become
 *    2026-02-28, 2026-01-28, 2025-12-28 instead of 2026-02-28, 2026-01-31, 2025-12-31, so every
 *    period after the first is permanently short and reads as perfectly ordinary.
 *
 * §3.2's bucket is half-open with the upper bound inclusive, so a period's <em>first included</em>
 * day is one after its exclusive lower edge — which is the day this prints, matching the engine's
 * own `From`.
 */
export function periodRanges(
  window: RatingWindow,
  periodLengthMonths: number | null,
  periodCount: number,
  asOf: string,
): RatingPeriodRange[] {
  const { firstIncluded, lastIncluded } = windowBounds(window, asOf);
  const ranges: RatingPeriodRange[] = [];

  for (let index = 1; index <= periodCount; index += 1) {
    if (periodLengthMonths === null) {
      // §4.5: a sumAll board over a fixed or all-time window normalises to one period with no
      // length, because there is no lower edge to compute. The period IS the window — and for
      // all-time it has no lower bound at all, which is reported as null rather than as a date the
      // engine does not apply.
      ranges.push({ index, from: firstIncluded, to: lastIncluded });
      continue;
    }

    const lowerExclusive = addMonths(lastIncluded, -index * periodLengthMonths);
    ranges.push({
      index,
      from: addDays(lowerExclusive, 1),
      to: addMonths(lastIncluded, -(index - 1) * periodLengthMonths),
    });
  }

  return ranges;
}

/**
 * §4.4's three window types as one pair of bounds, mirroring the engine's `Bounds`.
 *
 * <b>A fixed window is not capped at the anchor</b>, though §3.2's "an event dated after A is
 * excluded" reads as though it should be: §4.4 says the anchor "is still recorded but does not move
 * the window", and capping is precisely moving it. So an annual award read in March still describes
 * last year, which is the whole reason the mode exists.
 */
function windowBounds(
  window: RatingWindow,
  asOf: string,
): { firstIncluded: string | null; lastIncluded: string } {
  switch (window.type) {
    case 'rolling':
      return {
        // Exclusive lower edge plus a day, because it is the oldest period's lower edge and §3.2
        // makes those half-open — so window and period share an edge by construction rather than
        // by two calculations agreeing.
        firstIncluded: addDays(addMonths(asOf, -(window.lengthMonths ?? 0)), 1),
        lastIncluded: asOf,
      };
    case 'fixed':
      return { firstIncluded: window.startDate, lastIncluded: window.endDate ?? asOf };
    default:
      return { firstIncluded: null, lastIncluded: asOf };
  }
}

/**
 * `yyyy-mm-dd` plus or minus whole calendar months, <b>clamping into a short month</b> the way
 * `DateOnly.AddMonths` does. Done on the parts rather than through `Date`, whose `setMonth`
 * overflows — and which would also drag the value through a local timezone on the way.
 */
export function addMonths(iso: string, months: number): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];

  const zeroBased = month - 1 + months;
  const targetYear = year + Math.floor(zeroBased / 12);
  const targetMonth = ((zeroBased % 12) + 12) % 12;

  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();

  return format(targetYear, targetMonth + 1, Math.min(day, lastDay));
}

/** `yyyy-mm-dd` plus whole days. UTC throughout, so no local midnight can move it. */
export function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(year, month - 1, day + days));

  return format(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

function format(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// ---------------------------------------------------------------- §4.5 the period table

export type RatingAggregationMode = 'sumAll' | 'sumWithDecay' | 'bestNPerPeriod';

/**
 * One row of §9.3's table, as the operator edits it rather than as the document stores it.
 *
 * <b>`takeAll` is a draft-only field and is stripped on the way out.</b> §4.5 gives `n: null` the
 * meaning "every result in this period", which collides with the blank a number box reports while
 * somebody is halfway through typing — so a bare `n: number | null` would read a cleared box as a
 * deliberate "all", silently widening the period. Holding the operator's choice separately is the
 * same arrangement `toDefinitionDocument` uses: the editors keep whatever they need to switch back
 * and forth, and the document is trimmed on the way out.
 *
 * `decay` is nullable for the ordinary reason a number box is: a half-typed value is not a number,
 * and validation refuses a blank so one never reaches the server.
 */
export interface RatingPeriodDraft {
  index: number;
  /** §4.5's `n: null` — every result in the period — chosen deliberately rather than left blank. */
  takeAll: boolean;
  n: number | null;
  decay: number | null;
}

export interface RatingAggregationDraft {
  mode: RatingAggregationMode;
  periodLengthMonths: number | null;
  periods: RatingPeriodDraft[];
  tailPolicy: 'drop';
}

export interface BuilderIssue {
  severity: 'error' | 'warning';
  message: string;
}

/**
 * §9.3's lockstep: adding or removing a period moves `window.lengthMonths` with it.
 *
 * <b>A new period is the oldest one and inherits the ramp's own tail</b>, not full weight. A
 * seventh period seeded at `decay: 1.0` behind a ramp falling to 0.5 would make three-year-old
 * results the most valuable on the board — an inversion of the entire point of a rolling window,
 * produced by a button labelled "add a period" and visible only to somebody who reads the whole
 * column afterwards.
 */
export function setPeriodCount(
  window: RatingWindow,
  aggregation: RatingAggregationDraft,
  count: number,
): { window: RatingWindow; aggregation: RatingAggregationDraft } {
  const periods = aggregation.periods.slice(0, Math.max(1, count));
  const tail = aggregation.periods[aggregation.periods.length - 1];

  while (periods.length < count) {
    periods.push({
      index: periods.length + 1,
      takeAll: tail?.takeAll ?? false,
      n: tail?.n ?? null,
      decay: tail?.decay ?? 1,
    });
  }

  // §4.5: the indexes must run 1..K with no gaps or repeats, because §3.2 derives a period's months
  // from its index — a gap does not make an empty period, it makes months that score nothing while
  // the board still reads as having them. Rewritten from position rather than preserved, so no
  // removal can leave a hole.
  return withLength(window, { ...aggregation, periods: periods.map(reindex) });
}

/** The same lockstep from the other direction: six periods of three months is an 18-month window. */
export function setPeriodLength(
  window: RatingWindow,
  aggregation: RatingAggregationDraft,
  periodLengthMonths: number | null,
): { window: RatingWindow; aggregation: RatingAggregationDraft } {
  return withLength(window, { ...aggregation, periodLengthMonths });
}

function reindex(period: RatingPeriodDraft, position: number): RatingPeriodDraft {
  return { ...period, index: position + 1 };
}

/**
 * The window's length, recomputed from the table — on a rolling window only.
 *
 * A fixed window has no length to keep in step, and a `sumAll` board's single period takes the
 * window's own length rather than the other way about (§4.5), so neither is touched.
 */
function withLength(
  window: RatingWindow,
  aggregation: RatingAggregationDraft,
): { window: RatingWindow; aggregation: RatingAggregationDraft } {
  if (window.type !== 'rolling' || aggregation.mode === 'sumAll') return { window, aggregation };

  const length = aggregation.periodLengthMonths;
  if (length === null) return { window, aggregation };

  return {
    window: { ...window, lengthMonths: aggregation.periods.length * length },
    aggregation,
  };
}

/**
 * §4.4's multiple, checked <b>while the operator is still looking at the table</b> rather than at
 * save. §9.3 asks for exactly that, and §4.4 requires the message to name both numbers, because
 * what the operator has to decide is which of the two they meant.
 *
 * <b>Two guards, and both would otherwise fire on ordinary boards.</b> The rule is a rolling-window
 * rule — a fixed or all-time window has no length for the periods to be a multiple of — and
 * `sumAll` normalises to one period taking the window's own length, so its product is the length by
 * construction. The server guards the check in the same two places; a client stricter than that
 * would refuse the default board every room is created with (§2.6) and every annual award.
 */
export function coverageIssue(
  window: RatingWindow,
  aggregation: RatingAggregationDraft,
): BuilderIssue | null {
  if (window.type !== 'rolling' || aggregation.mode === 'sumAll') return null;
  if (window.lengthMonths === null || aggregation.periodLengthMonths === null) return null;

  const periodLength = aggregation.periodLengthMonths;
  const covered = aggregation.periods.length * periodLength;
  if (covered === window.lengthMonths) return null;

  const remainder = Math.abs(window.lengthMonths - covered);

  return {
    severity: 'error',
    message:
      `${aggregation.periods.length} periods of ${periodLength} months cover ${covered} months, ` +
      `but the window is ${window.lengthMonths}. They have to be equal: the ${remainder}-month ` +
      'remainder belongs to no period, and there is nothing defensible to do with it — dropping it ' +
      'loses results without saying so, and folding it into the last period makes that period a ' +
      'different length from every other.',
  };
}

// ---------------------------------------------------------------- §9.3 presets

/**
 * The three the schema accepts, exported so a preset cannot quietly introduce a fourth. §3.5 makes
 * these one shape — normalised to a period array at write time, with the engine never branching on
 * the value — so a mode the normaliser has no arm for does not degrade, it throws.
 */
export const AGGREGATION_MODES: readonly RatingAggregationMode[] = [
  'sumAll',
  'sumWithDecay',
  'bestNPerPeriod',
];

export interface RatingPreset {
  id: 'gpiStyle' | 'simpleDecay' | 'noDecay';
  label: string;
  /** One line saying what it does to a rating, in an operator's terms rather than the schema's. */
  detail: string;
  apply: (window: RatingWindow) => { window: RatingWindow; aggregation: RatingAggregationDraft };
}

/**
 * §9.3's three presets. <b>A preset WRITES the period array and is never stored.</b>
 *
 * There is no fourth aggregation mode hiding in here and there must never be one: §3.5 normalises
 * three modes into one period array, and a stored preset id would be a second answer to what the
 * board does — free to disagree with the table the operator edited straight afterwards, and
 * invisible to the engine, which reads the periods.
 *
 * Each takes the current window so it can move it in step (§9.3): GPI-style is a 36-month
 * methodology and says so by writing the window, while <i>No decay</i> is a statement about
 * aggregation alone and leaves the window the operator chose.
 */
export const RATING_PRESETS: readonly RatingPreset[] = [
  {
    id: 'gpiStyle',
    label: 'GPI-style',
    detail:
      'Three years in six half-year periods. The best 5 results count in each of the recent three ' +
      'and the best 4 in the older three, each period worth a little less than the one before it.',
    apply: () => ({
      window: { type: 'rolling', lengthMonths: 36, startDate: null, endDate: null },
      aggregation: {
        mode: 'bestNPerPeriod',
        periodLengthMonths: 6,
        // §5.2's reference board exactly — the same six periods, the same 5/5/5/4/4/4, the same
        // ramp. §5.3 is clear that the window and the counts are GPI's published methodology while
        // the ramp is invented; both are reproduced here because the preset claims the name.
        periods: [
          { index: 1, takeAll: false, n: 5, decay: 1 },
          { index: 2, takeAll: false, n: 5, decay: 0.9 },
          { index: 3, takeAll: false, n: 5, decay: 0.8 },
          { index: 4, takeAll: false, n: 4, decay: 0.7 },
          { index: 5, takeAll: false, n: 4, decay: 0.6 },
          { index: 6, takeAll: false, n: 4, decay: 0.5 },
        ],
        tailPolicy: 'drop',
      },
    }),
  },
  {
    id: 'simpleDecay',
    label: 'Simple decay',
    detail:
      'Two years in four half-year periods. Every result counts, but an older one counts for less ' +
      '— no cut-off to explain, and nobody is penalised for playing a lot.',
    apply: () => ({
      window: { type: 'rolling', lengthMonths: 24, startDate: null, endDate: null },
      aggregation: {
        mode: 'sumWithDecay',
        periodLengthMonths: 6,
        // §4.5: sumWithDecay keeps every result in each period, so `n` is null throughout — and the
        // server REFUSES an n set under it rather than discarding one, because discarding would
        // score the board on a rule the operator did not ask for. `takeAll` is what spells that.
        periods: [
          { index: 1, takeAll: true, n: null, decay: 1 },
          { index: 2, takeAll: true, n: null, decay: 0.75 },
          { index: 3, takeAll: true, n: null, decay: 0.5 },
          { index: 4, takeAll: true, n: null, decay: 0.25 },
        ],
        tailPolicy: 'drop',
      },
    }),
  },
  {
    id: 'noDecay',
    label: 'No decay',
    detail:
      'Every result in the window, at full weight, however old. The simplest thing a rating can ' +
      'do — and the one the default All Tournaments rating does.',
    // The window is left exactly as it was: this is a statement about how results are weighed, not
    // about how far back to look. Overwriting a 36-month window an operator had chosen because they
    // pressed a button about decay is the kind of surprise a preset must not spring.
    apply: (window) => ({
      window,
      aggregation: {
        mode: 'sumAll',
        // §4.5: a sumAll board normalises to a single period taking the WINDOW's own length, so the
        // two agree by construction and §4.4's multiple cannot fail. Null under a fixed or all-time
        // window, where there is no lower edge to compute.
        periodLengthMonths: window.type === 'rolling' ? window.lengthMonths : null,
        periods: [{ index: 1, takeAll: true, n: null, decay: 1 }],
        tailPolicy: 'drop',
      },
    }),
  },
];

// ---------------------------------------------------------------- §4.1 the documents to send

/**
 * §4.5's document, trimmed the way `toDefinitionDocument` trims a definition: the schema closes
 * `additionalProperties`, so a key left over from the draft is a 400 rather than an ignored extra.
 *
 * Two things are dropped here and both are draft-only. `takeAll` is the operator's choice between
 * "every result" and "the best n", which the document spells as a null `n` — and holding it
 * separately is what stops a half-typed number box reading as a deliberate "all". And nothing names
 * the preset that wrote the table, because §9.3 makes a preset an action rather than a setting.
 */
export function toAggregationDocument(
  aggregation: RatingAggregationDraft,
  window: RatingWindow,
): Record<string, unknown> {
  return {
    mode: aggregation.mode,
    periodLengthMonths:
      aggregation.mode === 'sumAll'
        ? window.type === 'rolling'
          ? window.lengthMonths
          : null
        : aggregation.periodLengthMonths,
    periods: aggregation.periods.map((period) => ({
      index: period.index,
      // §4.5's null — every result in the period. `sumAll` and `sumWithDecay` are null throughout
      // whatever the row holds, because the server refuses an `n` under either rather than
      // discarding it, and a row that kept one from before a mode switch would be unsaveable.
      n: period.takeAll || aggregation.mode !== 'bestNPerPeriod' ? null : period.n,
      decay: aggregation.mode === 'sumAll' ? 1 : period.decay,
    })),
    tailPolicy: aggregation.tailPolicy,
  };
}

// ---------------------------------------------------------------- §9.4, §1.3, §12

/**
 * §9.4's required notice: <b>the rating engine does not read `definition.aggregation`.</b>
 *
 * §1.3 is normative — a points system's `sumAll` or `bestN(15)` is a leaderboard instruction, and
 * the rating board's own block replaces it entirely. The alternative (honour the definition when
 * the board leaves its own unset) was rejected because it puts one decision in two places free to
 * disagree, and §1.3 records the mitigation as <em>this notice</em> rather than a change to the
 * rule. So this is required, not decorative: without it an operator who configured a top-15 system
 * finds ratings quietly ignoring it, and nothing anywhere would have told them.
 *
 * Silent on `sumAll`, which is §9.4's own condition. A sumAll system is already doing what the
 * rating's block would tell it to, so there is nothing surprising to report — and a notice beside
 * every points system is one nobody reads by the time one of them matters.
 */
export function aggregationNotice(
  aggregation: { mode: string; n?: number } | null,
): BuilderIssue | null {
  if (aggregation === null || aggregation.mode === 'sumAll') return null;

  const n = aggregation.n ?? 0;
  const what =
    aggregation.mode === 'bestN' ? `best ${n} event${n === 1 ? '' : 's'}` : aggregation.mode;

  return {
    severity: 'warning',
    message:
      `This system aggregates ${what} when used on a leaderboard. Ratings use their own ` +
      'aggregation, configured below — the system’s setting does not apply here.',
  };
}

/**
 * §1.3's one hard refusal, surfaced beside the picker rather than met at save.
 *
 * `earliestAchieved` is meaningless on a rating and the server refuses a board pinning a definition
 * that uses it. Why it differs from a leaderboard: a leaderboard total only rises, so "who reached
 * this total first" names a real moment; a rating total is not monotonic — the rolling window drops
 * a player's best result the month it ages out, and decay lowers every survivor at each period
 * boundary — so a player passes any given total repeatedly in both directions, and the earliest
 * time they held it is an artefact of the calendar rather than an achievement.
 */
export function tiebreakerProblem(tiebreakers: readonly string[] | null): BuilderIssue | null {
  if (tiebreakers === null || !tiebreakers.includes('earliestAchieved')) return null;

  return {
    severity: 'error',
    message:
      'This system breaks ties on who reached a total earliest, which a rating cannot honour. A ' +
      'leaderboard total only rises, so that names a real moment; a rating total goes down as well ' +
      'as up — results age out of the window and decay each period — so a player passes the same ' +
      'total again and again. Pick another system, or publish a version of this one without that ' +
      'tiebreaker.',
  };
}

/**
 * §12's participation hazard. <b>A warning, never a refusal</b> — a room scoring its own sheets,
 * where every non-cashing row is a real entry, is doing something perfectly reasonable.
 *
 * The defect is fantasy Finding 3: a board scored 340 players where 253 cashed, because
 * participation points were awarded against synthetic attendance. Imported fields seed their
 * non-cashing entrants from the roster so the entry total balances against the published field
 * size — so the count is real while the identity each row was given is a random allocation. Under
 * `participation.mode: "none"` those rows produce no award and contribute exactly zero; under
 * either other mode they reach the rating itself. §12 says the rolling window amplifies it, because
 * an allocation lands in a decay band and stays there for the whole window.
 *
 * §5.1's reference system sets `none` for exactly this reason, which is why the silent case is the
 * control rather than an afterthought.
 */
export function participationNotice(mode: string | null): BuilderIssue | null {
  if (mode === null || mode === 'none') return null;

  return {
    severity: 'warning',
    message:
      'This system gives points to entries that did not cash. On results a room entered itself ' +
      'that is exactly right. On imported fields it is not: the non-cashing rows are seeded from ' +
      'the roster so the entry count balances, so the number of them is real but which player each ' +
      'one names is not — and those points reach the rating rather than only its event count. A ' +
      'rolling window keeps each one in its decay band for the whole window.',
  };
}

// ---------------------------------------------------------------- §4.7, §2.6, the card line

/**
 * §4.7's four levels, in order. **Cumulative — `Room` ⊆ `Players` ⊆ `Public`** — so each widens the
 * audience and none narrows it, and room staff read their own board at every level.
 */
export const VISIBILITY_ORDER = ['private', 'room', 'players', 'public'] as const;

export type RatingVisibility = (typeof VISIBILITY_ORDER)[number];

/**
 * §4.7's confirmation before a rating is published to players or to the public.
 *
 * **What is being confirmed is a disclosure about named individuals**, which is why the sentence
 * names the board and the number of people in it rather than the setting. A room may be perfectly
 * willing to rank its regulars in the back office and not at all willing to publish that ranking;
 * those are different decisions and only the second needs stopping at.
 *
 * **Null on the way down, and that asymmetry is the rule rather than an omission.** Narrowing an
 * audience takes something back, which nobody needs protecting from — and a confirmation on every
 * change would train an operator to click through the one that matters. Null too for Private →
 * Room: that widens the audience only to the staff who already administer the board, so nobody
 * outside the room learns anything.
 *
 * A `playerCount` of null is **said**, not dropped. The count is the whole point of the sentence,
 * and a confirmation that quietly omitted it would read as a board about nobody in particular.
 */
export function visibilityConfirmation(
  from: RatingVisibility,
  to: RatingVisibility,
  boardName: string,
  playerCount: number | null,
): { title: string; body: string } | null {
  const widening = VISIBILITY_ORDER.indexOf(to) > VISIBILITY_ORDER.indexOf(from);
  if (!widening || (to !== 'players' && to !== 'public')) return null;

  const who =
    to === 'players'
      ? 'every player holding a card at this room'
      : 'anyone at all, including people who are not signed in';

  const people =
    playerCount === null
      ? 'It ranks the players this rating covers — how many has not been computed yet.'
      : `It ranks ${playerCount.toLocaleString('en-US')} named ${playerCount === 1 ? 'player' : 'players'}.`;

  return {
    title: `Publish “${boardName}” to ${to === 'players' ? 'players' : 'the public'}?`,
    body:
      `“${boardName}” will be readable by ${who}. ${people} This is the room’s assessment of ` +
      'people by name, so it is worth being sure before it leaves the back office. You can narrow ' +
      'it again at any time, and narrowing needs no confirmation.',
  };
}

export type BoardSection =
  | 'name'
  | 'pointsSystem'
  | 'scope'
  | 'cohort'
  | 'window'
  | 'aggregation'
  | 'qualification'
  | 'visibility';

/**
 * Which sections this board may not edit, and **why** — §2.6's default-board locks and §2.4's pin.
 *
 * A locked control is shown and disabled carrying its reason, never hidden: a control that vanishes
 * says the feature does not exist, and a greyed one with no explanation says "not now" where the
 * truth is "never". §9.9 made exactly this call for a pinned points system.
 *
 * **Two different rules, and they are easy to conflate.** §2.6 locks the default board's name,
 * scope and cohort — permanently, at every status, because those three are what make it *the*
 * default; a default that could be narrowed would mean whatever the last operator narrowed it to,
 * and a narrower rating is a separate board. §2.4's pin locks the points system on *any* board the
 * moment it stops being a Draft, because the selection becomes the thing every published standing
 * was computed from.
 *
 * Everything else stays open on every board at every status, which is §2.4 in as many words: an
 * operator legitimately needs to add a late event or fix a cohort typo on a running rating.
 */
export function sectionLock(
  section: BoardSection,
  board: { isDefault: boolean; status: string },
): { locked: boolean; reason: string | null } {
  if (section === 'pointsSystem' && board.status !== 'draft') {
    return {
      locked: true,
      reason:
        'Starting this rating pinned the points system version, and that pin never changes — ' +
        'every standing it has published was computed with it.',
    };
  }

  if (board.isDefault && (section === 'name' || section === 'scope' || section === 'cohort')) {
    return {
      locked: true,
      reason:
        'This is the room’s default rating, so its name, the events it covers and the players it ' +
        'covers are fixed — that is what makes it the default. For a rating over fewer events or ' +
        'fewer players, add a rating of your own beside it.',
    };
  }

  return { locked: false, reason: null };
}

/**
 * The sentence under a board's name on the ratings list.
 *
 * **It used to read "Scoring with points system version 1", and both halves were wrong for the
 * state they described.** Present tense, so a Draft board — which scores nothing and has published
 * nothing — read as one that is running; and a bare version number, so the operator was never told
 * *what* it would score with. ADR 0011 Decision 12 pre-selects §5.1's GPI-style system precisely so
 * a new room has something to start from, and that promise was invisible on the one screen that
 * mentioned it.
 *
 * **The name is joined on the client rather than carried on `RatingBoardDto`**, and the honest
 * degradation is the reason as much as the cost. `usePointsSystems(roomId)` returns exactly the
 * systems this room may select, so a lookup that misses *is* the answer "you can no longer see
 * this" — where a server-side `PointsSystemName` would resolve a name regardless and leave the
 * screen naming a system that is not in its own picker. Hence the two absences are told apart:
 * `systems === null` is a catalogue that has not loaded, and saying anything about reach there
 * would be a lie for a fifth of a second on every page load.
 */
export function pointsSystemLine(
  board: { status: string; pointsSystemId: string | null; pointsSystemVersionNumber: number | null },
  systems: readonly { id: string; name: string; isPlatform: boolean }[] | null,
): string {
  if (board.pointsSystemId === null || board.pointsSystemVersionNumber === null) {
    return 'No points system chosen yet — the rating cannot be computed until there is one.';
  }

  const version = `v${board.pointsSystemVersionNumber}`;
  const system = systems?.find((s) => s.id === board.pointsSystemId) ?? null;

  const named =
    system !== null
      ? `${system.name} ${version}`
      : systems === null
        ? `Points system ${version}`
        : `${version} of a points system this room can no longer see`;

  // §2.4: a Draft board's version is a SELECTION and a started one's is the pin. The tense is the
  // load-bearing half — a Draft scores nothing until somebody presses Start, and saying it is
  // scoring is the reading that leaves an operator waiting for standings that will never arrive.
  return board.status === 'draft'
    ? `${named} — pre-selected. Not running until you start it.`
    : `Pinned to ${named}.`;
}

// ---------------------------------------------------------------- §4.1 the remaining documents

export interface RatingCohortDraft {
  city: string[];
  state: string[];
  country: string[];
  unknownPolicy: 'exclude' | 'include';
}

export interface RatingQualificationDraft {
  minEvents: number | null;
  minCashes: number | null;
  unqualifiedDisplay: 'provisional' | 'hidden' | 'ranked';
}

/**
 * §4.4's document, **trimmed to the branch in force**.
 *
 * Each of the three branches constrains the other two's fields to null, so a `lengthMonths` left
 * behind after switching to a fixed window is a 400 rather than an ignored extra. The draft keeps
 * all of them on purpose — switching rolling → fixed → rolling must not lose the length somebody
 * typed — and the trimming happens here, which is `toDefinitionDocument`'s arrangement exactly.
 */
export function toWindowDocument(window: RatingWindow): Record<string, unknown> {
  return {
    type: window.type,
    lengthMonths: window.type === 'rolling' ? window.lengthMonths : null,
    startDate: window.type === 'fixed' ? window.startDate : null,
    endDate: window.type === 'fixed' ? window.endDate : null,
  };
}

/**
 * §4.3's document. Blank entries are dropped and the rest trimmed, because the schema requires
 * `minLength: 1` and `uniqueItems` — and because a stray empty string would be a constraint on a
 * field nobody meant to constrain, which under the default `exclude` policy silently drops every
 * player the room holds no value for.
 */
export function toCohortDocument(cohort: RatingCohortDraft): Record<string, unknown> {
  const clean = (values: readonly string[]) => [
    ...new Set(values.map((v) => v.trim()).filter((v) => v.length > 0)),
  ];

  return {
    city: clean(cohort.city),
    state: clean(cohort.state),
    country: clean(cohort.country),
    unknownPolicy: cohort.unknownPolicy,
  };
}

/**
 * §4.6's document. **`countBasis` is written here rather than offered as a control**, and that is
 * the client half of a rule §4.6 states twice over.
 *
 * `distinctEventsEntered` is the only accepted value: the `Buyins` column is synthetic in imported
 * data — 143,541 of 179,924 rows in the filled WSOP dataset are seeded non-cashing entrants — so a
 * denominator of entries is scoreable on one dataset and fictional on another. The server refuses
 * `entries` explicitly, ahead of the schema, so that the refusal can carry that reason; §4.6 is
 * clear that absence of the option is not enough, because somebody adds it back.
 */
export function toQualificationDocument(
  qualification: RatingQualificationDraft,
): Record<string, unknown> {
  return {
    minEvents: qualification.minEvents ?? 0,
    minCashes: qualification.minCashes ?? 0,
    countBasis: 'distinctEventsEntered',
    unqualifiedDisplay: qualification.unqualifiedDisplay,
  };
}

/**
 * §4.2's intersection notice: a scope carrying dates <em>and</em> a rolling window means the events
 * rated are the intersection of the two, which is narrower than either — and §4.2 requires the
 * effective range shown, "because an operator who sets both and sees fewer events than expected
 * will not guess why".
 *
 * **Guarded on the scope really having a bound**, matching the server. The same warning used to
 * fire on every rolling board, because before the bounds could be null every rating scope really
 * did carry two date ranges — honest at the time, and precisely how a notice stops being read. The
 * board it most needs to be believed on is the one that has two.
 */
export function effectiveRangeNotice(
  scope: { startDate: string | null; endDate: string | null },
  window: RatingWindow,
  asOf: string,
): BuilderIssue | null {
  if (window.type !== 'rolling') return null;
  if (scope.startDate === null && scope.endDate === null) return null;

  const windowStart = addDays(addMonths(asOf, -(window.lengthMonths ?? 0)), 1);
  const from = scope.startDate !== null && scope.startDate > windowStart ? scope.startDate : windowStart;
  const to = scope.endDate !== null && scope.endDate < asOf ? scope.endDate : asOf;

  return {
    severity: 'warning',
    message:
      `This rating has both a rolling window and its own dates, so what it actually covers is the ` +
      `narrower of the two: ${from} to ${to} as of today. That is fewer events than either setting ` +
      'reads as on its own. Clear the dates above to let the window decide on its own.',
  };
}

// ---------------------------------------------------------------- what blocks the save

export interface SectionIssue extends BuilderIssue {
  section: BoardSection;
}

export interface BoardValidationInput {
  name: string;
  window: RatingWindow;
  aggregation: RatingAggregationDraft;
  qualification: RatingQualificationDraft;
}

/**
 * Everything the server would refuse, said while the operator is still looking at the control.
 *
 * Feedback, never the control — the same relationship `validate.ts` has to
 * `PointsSystemDefinitionValidator`. What it buys is the round trip and the section to go and fix,
 * and **every offender at once rather than the first**: a form with four problems answered one at a
 * time is four round trips, and by the third the operator has concluded the app is broken.
 */
export function validateBoard(draft: BoardValidationInput): SectionIssue[] {
  const issues: SectionIssue[] = [];
  const push = (section: BoardSection, message: string) =>
    issues.push({ section, severity: 'error', message });

  if (draft.name.trim().length === 0) push('name', 'Give the rating a name.');
  else if (draft.name.length > 200) push('name', 'The name is longer than 200 characters.');

  if (draft.window.type === 'rolling' && (draft.window.lengthMonths ?? 0) < 1) {
    push('window', 'A rolling window has to look back at least one month.');
  }

  if (draft.window.type === 'fixed') {
    const { startDate, endDate } = draft.window;
    if (startDate === null || endDate === null) {
      push('window', 'A fixed window needs both a first and a last day.');
    } else if (endDate < startDate) {
      push('window', `The last day (${endDate}) is before the first (${startDate}).`);
    }
  }

  const coverage = coverageIssue(draft.window, draft.aggregation);
  // Reported against the WINDOW rather than the periods, because the window length is the number an
  // operator typed and the period count is the one they will have to change to match it — and
  // §4.4's message names both, so it reads correctly from either.
  if (coverage) push('window', coverage.message);

  for (const period of draft.aggregation.periods) {
    if (period.decay === null) {
      push('aggregation', `Period ${period.index} has no decay. Use 1 for full weight.`);
    } else if (period.decay < 0 || period.decay > 1) {
      // §4.5: above 1.0 would make old results worth more than new ones, which inverts the point of
      // a rolling window. Refused rather than clamped, because a clamp scores a season on a number
      // nobody chose.
      push(
        'aggregation',
        `Period ${period.index}'s decay is ${period.decay}. It runs from 0 (worth nothing) to 1 ` +
          '(full weight) — above 1 would make older results worth more than recent ones.',
      );
    }

    if (draft.aggregation.mode === 'bestNPerPeriod' && !period.takeAll && period.n === null) {
      // §4.5 gives a null `n` the meaning "every result in the period", so a blank box is not an
      // answer here — it is the one value that would silently mean the opposite of what the row is
      // set to. `takeAll` is what makes "every result" sayable.
      push(
        'aggregation',
        `Period ${period.index} counts the best results but does not say how many. Put a number ` +
          'in, or switch the row to counting every result.',
      );
    }

    if (
      draft.aggregation.mode === 'bestNPerPeriod' &&
      !period.takeAll &&
      period.n !== null &&
      period.n < 1
    ) {
      push('aggregation', `Period ${period.index} has to count at least one result.`);
    }
  }

  if ((draft.qualification.minEvents ?? 0) < 0 || (draft.qualification.minCashes ?? 0) < 0) {
    push('qualification', 'A minimum cannot be negative. Use 0 to rate everybody.');
  }

  return issues;
}

/**
 * §9.6's disclosure, beside the cohort editor and **not behind a tooltip**.
 *
 * §4.3 makes this intended behaviour and says so twice: the demographic comes from the Player
 * record and never from the result row, because a cohort that depended on where somebody lived when
 * they played is incoherent as a measure of the person. The consequence is genuinely surprising —
 * last month's Florida rating and this month's recompute will disagree about a player who moved,
 * and the whole of their history moves with them — which is why §9.6 requires it on screen. An
 * operator who meets it first in a support ticket will not believe it.
 */
export const COHORT_DISCLOSURE =
  'Cohorts read each player’s current city and state from their player card. A player who updates ' +
  'their details moves between cohorts, and their full history moves with them — so a rating for ' +
  'Florida players will stop counting somebody the day they tell you they have moved to Nevada, ' +
  'including the results they scored while they lived here. That is deliberate: a rating is about ' +
  'the person now.';

// ---------------------------------------------------------------- reading a stored board

/**
 * The four blocks read back off a stored board. (`Scope` is the fifth and is read by
 * `readScope` in `@brush/ui`, which §9.2 requires the builder to share with the leaderboard.)
 *
 * **These are opaque documents on the wire** — `JsonElement` on the server, `unknown` in the
 * generated client — so nothing is guaranteed about their shape, including that they came from this
 * build. Each reader therefore falls back to a legal draft rather than to `undefined`: a board
 * written under a later schema must render wrong at worst, never make itself unreachable.
 */
function record(raw: unknown): Record<string, unknown> {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function int(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

function text(raw: unknown): string | null {
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

function strings(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
}

export function readWindow(raw: unknown): RatingWindow {
  const document = record(raw);
  const type = document.type;

  return {
    type: type === 'fixed' || type === 'allTime' ? type : 'rolling',
    lengthMonths: int(document.lengthMonths) ?? 12,
    startDate: text(document.startDate),
    endDate: text(document.endDate),
  };
}

export function readAggregation(raw: unknown): RatingAggregationDraft {
  const document = record(raw);
  const mode = AGGREGATION_MODES.includes(document.mode as RatingAggregationMode)
    ? (document.mode as RatingAggregationMode)
    : 'sumAll';

  const stored = Array.isArray(document.periods) ? document.periods : [];
  const periods: RatingPeriodDraft[] = stored.map((entry, position) => {
    const period = record(entry);
    const n = int(period.n);

    return {
      // Rewritten from position rather than trusted, for the reason §4.5 gives: §3.2 derives a
      // period's months from its index, so a gap or a repeat in a stored document would put the
      // table and the engine on different months. The validator on the server refuses one; the
      // builder should not be the thing that shows it.
      index: position + 1,
      // §4.5's null — "every result in this period" — recovered as the deliberate choice it is
      // rather than as an empty number box. This is the one place the draft and the document
      // genuinely differ, and reading it back wrong makes a saved board unsaveable.
      takeAll: n === null,
      n,
      decay: int(period.decay) ?? 1,
    };
  });

  return {
    mode,
    periodLengthMonths: int(document.periodLengthMonths),
    periods: periods.length > 0 ? periods : [{ index: 1, takeAll: true, n: null, decay: 1 }],
    tailPolicy: 'drop',
  };
}

export function readCohort(raw: unknown): RatingCohortDraft {
  const document = record(raw);

  return {
    city: strings(document.city),
    state: strings(document.state),
    country: strings(document.country),
    // §4.3's default, and the safe one: 143 of 1,398 SHRPO identities carry a blank city or state,
    // and defaulting to `include` would put every one of them in a "Florida players" rating and
    // assert something the data does not support.
    unknownPolicy: document.unknownPolicy === 'include' ? 'include' : 'exclude',
  };
}

export function readQualification(raw: unknown): RatingQualificationDraft {
  const document = record(raw);
  const display = document.unqualifiedDisplay;

  return {
    minEvents: int(document.minEvents) ?? 0,
    minCashes: int(document.minCashes) ?? 0,
    unqualifiedDisplay: display === 'hidden' || display === 'ranked' ? display : 'provisional',
  };
}

/** §4.7's four levels, read back with the same fall-through the others use. */
export function readVisibility(raw: unknown): RatingVisibility {
  return VISIBILITY_ORDER.includes(raw as RatingVisibility) ? (raw as RatingVisibility) : 'private';
}

/**
 * Why the preview pane cannot compute, when the reason is the points system — **read from the draft
 * as well as from the board, which is the whole of the fix.**
 *
 * <b>It was the stored board alone, so it told an operator who had just chosen a system in section 2
 * to go and choose one in section 2.</b> The refusal itself is correct: `/compute` scores the board
 * as stored, and an unsaved choice is not stored, so the server genuinely has none. What was wrong
 * was the instruction — it named the step already taken instead of the one remaining.
 *
 * <b>A board with a saved system gets no hint at all</b>, which is the control. A refusal then has
 * some other cause — no events in scope, a platform template, an entitlement — and blaming the
 * points system would send somebody to a section that is already right. The pane shows the server's
 * own message alone, which is the useful half.
 *
 * Takes the two ids rather than the board and the draft, so the one thing it depends on is in the
 * signature and neither shape can drift past it.
 */
export function previewUnavailableHint(systems: {
  /** What the board carries in the database — what `/compute` actually scored. */
  stored: string | null;
  /** What section 2 names on screen right now. */
  draft: string | null;
}): string | null {
  if (systems.stored !== null) return null;

  return systems.draft !== null
    ? 'A points system is chosen but not saved yet, and the preview is computed from this rating ' +
        'as saved. Save to compute with it.'
    : 'A rating cannot be computed until a points system is chosen — section 2 above.';
}
